// segmentation.js — الواجهة تعرض ولا تعرّف ولا تصنّف (A3 · 2026-10-12)
//
// قرار المالك: هذا الملف يحوي **صفر حدود شريحة وصفر تصنيف محلي**. لا أرقام
// يومية ولا زيارات ولا إنفاق ولا AOV، ولا دالة تُعيد شريحة عميل من إشاراته
// الخام. كل عميل يصل من الخادم بحقل `segment` جاهز (classify_population /
// core.customer_population) — هذا الملف يعرض فقط: label/emoji/color/desc
// لكود شريحة مُعطى، مصدرها الوحيد `GET /api/segments/definitions`.
//
// A3 (2026-10-12) — حُذفت دالة التصنيف المحلية ومحرّك القواعد
// (_signalValue/_ruleHolds/_matches) نهائياً. كانت (منذ v200.33) تقرأ قواعد
// مهيكلة من الخادم فتبدو "آمنة"، لكنها بقيت تصنيفاً ثالثاً موازياً مكتوباً
// بلغة مختلفة (JS) عن classify_rfm (Python) — أي فرق دلالي بينهما (ترتيب
// تقييم، تعادل، صفر زيارات) يُنتج شريحة مختلفة في الواجهة عن الخادم بصمت.
// المستهلكون (CustomersSmart، CustomerSurveys) يقرأون `c.segment` من
// استجابة الخادم مباشرة الآن.
//
// عند الإقلاع أوّل مرة يجلب الملف التعريفات (للعرض فقط) ويُخزّنها في cache.
// • جاري التحميل/فشل الجلب → تُظهر الواجهة رسالة صريحة عبر
//   `<SegmentsLoadGate>` أو `useSegmentsStatus()` — لا افتراضيات.
//
// حرّاس دائمة:
//   test_segments_single_source_guard.py         → صفر حدود محلية
//   test_segments_structured_rules_guard.py      → صيغة all/any، مفردات مغلقة
//   test_migration_parity.py                     → عميل بعميل قبل/بعد
//   test_no_logic_in_display_fields.py           → صفر regex على حقول العرض
//   test_a3_no_segment_classification_logic_in_frontend_guard.py → صفر
//     تصنيف محلي (عتبات recency/visits/aov) في كل frontend/src، لا هنا فقط

import { api } from "./api";

export function daysSince(dateIso) {
  if (!dateIso) return Infinity;
  const t = Date.parse(dateIso);
  if (isNaN(t)) return Infinity;
  return Math.floor((Date.now() - t) / 86400000);
}

export function avgPerVisit(c) {
  const v = Number(c.visits) || 0;
  if (v === 0) return 0;
  return (Number(c.totalSpent) || 0) / v;
}

// ─── Runtime cache — populated once from the server ────────────────────
let _defs = null;              // مصفوفة تعريفات مرتبة بالأولوية
let _defsById = new Map();     // meta lookup by code
let _loadPromise = null;
let _loadStatus = "idle";      // "idle" | "loading" | "ready" | "error"
let _loadError = null;         // Error object لآخر محاولة فاشلة
const _statusListeners = new Set();

function _notify() {
  for (const fn of _statusListeners) {
    try { fn({ status: _loadStatus, error: _loadError }); } catch (_) { /* ignore */ }
  }
}

export function subscribeSegmentsStatus(fn) {
  _statusListeners.add(fn);
  return () => _statusListeners.delete(fn);
}

export function getSegmentsStatus() {
  return { status: _loadStatus, error: _loadError };
}

/** يجلب التعريفات من الخادم مرّة واحدة ويُخزّنها.
 *  عند الفشل: يُصفّي الحالة إلى "error" دون أي fallback أو تعريف افتراضي. */
export async function initSegmentsFromServer() {
  if (_loadStatus === "ready") return _defs;
  if (_loadPromise) return _loadPromise;
  _loadStatus = "loading";
  _loadError = null;
  _notify();
  _loadPromise = (async () => {
    try {
      const r = await api.get("/segments/definitions");
      const rows = Array.isArray(r?.data?.definitions) ? r.data.definitions : [];
      if (!rows.length) {
        throw new Error("empty_definitions_response");
      }
      const sorted = [...rows].sort((a, b) => (a.priority || 999) - (b.priority || 999));
      _defs = sorted;
      _defsById = new Map(sorted.map((d) => [d.code, d]));
      _loadStatus = "ready";
      _loadError = null;
      _notify();
      return sorted;
    } catch (e) {
      _defs = null;
      _defsById = new Map();
      _loadStatus = "error";
      _loadError = e || new Error("segments_load_failed");
      _notify();
      throw _loadError;
    } finally {
      _loadPromise = null;
    }
  })();
  return _loadPromise;
}

export function retrySegmentsLoad() {
  _loadStatus = "idle";
  _loadError = null;
  _loadPromise = null;
  return initSegmentsFromServer();
}

// أطلق الجلب فور الاستيراد — الحالة تُنشر عبر subscribeSegmentsStatus.
// نمتصّ الخطأ هنا كي لا يُطلق unhandledrejection؛ الاشتراك يكشفه للـUI.
initSegmentsFromServer().catch(() => { /* consumed via status listeners */ });

/** Metadata (label/color/desc) لعرض شريحة — بلا أي حدّ محلي. */
export function getSegmentMeta(id) {
  const d = _defsById.get(id);
  if (!d) {
    return {
      id,
      code: id,
      label: `❓ ${id}`,
      color: "var(--brown)",
      desc: "",
    };
  }
  return {
    id: d.code,
    code: d.code,
    label: `${d.emoji || ""} ${d.label_ar}`.trim(),
    color: d.color || "var(--brown)",
    desc: d.description_ar || "",
  };
}

/** كل تعريفات الشرائح مرتَّبة بالأولوية — لأي ملف يحتاج قائمة كاملة
 *  (ترتيب/تسميات/ألوان) بدل بناء مصفوفة محلية (A3، البند 3ب). */
export function getAllSegmentDefs() {
  if (!_defs || _defs.length === 0) return [];
  return _defs.map((d) => ({
    code: d.code,
    label: d.label_ar || d.code,
    emoji: d.emoji || "",
    color: d.color || "var(--brown)",
    desc: d.description_ar || "",
  }));
}

// ─── Behavior TAGS — display-only tags, no thresholds ──────────────────
// v200.32 · الأعتاب الكمية (100/60) أزيلت إلى بند مستقل.
// tags السلوكية تعتمد على DATA فقط (shift counts, day counts) لا حدوداً
// قابلة للتعديل الإداري. تبقى للعرض السلوكي دون تصنيف مالي/شرائحي.
export function computeBehaviorTags(c) {
  const tags = [];
  const sh = c.shiftCounts || c.shifts || null;
  if (sh && typeof sh === "object") {
    const s1 = sh.S1 || sh.morning || 0;
    const s2 = sh.S2 || sh.evening || 0;
    const s3 = sh.S3 || sh.late || 0;
    const total = s1 + s2 + s3;
    if (total >= 3) {
      if (s1 / total > 0.55) tags.push({ id: "morning", label: "☀ صباحي", color: "#F59E0B" });
      else if (s3 / total > 0.45) tags.push({ id: "latenight", label: "🌙 ليلي", color: "#1E3A8A" });
      else if (s2 / total > 0.5) tags.push({ id: "evening", label: "🌆 مسائي", color: "#0891B2" });
    }
  }
  const dc = c.dayCounts || null;
  if (dc && typeof dc === "object") {
    const weekend = (dc.thu || 0) + (dc.fri || 0) + (dc.sat || 0);
    const total = Object.values(dc).reduce((a, b) => a + (Number(b) || 0), 0);
    if (total >= 3 && weekend / total > 0.55) {
      tags.push({ id: "weekend", label: "🎉 نهاية أسبوع", color: "#EC4899" });
    }
  }
  return tags;
}

// ─── Public API ──────────────────────────────────────────────────────────

/** Segments list — مبنية من التعريفات المُحمَّلة من الخادم. لا صلبة. */
function _buildDisplaySegments() {
  const list = [{ id: "all", code: "all", label: "👥 الكل",
                  color: "var(--brown)", desc: "كل العملاء",
                  filter: () => true }];
  if (!_defs || _defs.length === 0) return list;
  for (const d of _defs) {
    list.push({
      id: d.code,
      code: d.code,
      label: `${d.emoji || ""} ${d.label_ar}`.trim(),
      color: d.color || "var(--brown)",
      desc: d.description_ar || "",
      filter: (c) => c.segment === d.code,
    });
  }
  return list;
}

export const METHODOLOGIES = [
  {
    id: "saudi_pro",
    label: "🇸🇦 السعودي الاحترافي (الموصى به)",
    short: "السعودي",
    desc: "نموذج RFM مخصّص للسوق السعودي — تعريفات canonical من الخادم.",
    // getter to always read the current cache.
    get segments() { return _buildDisplaySegments(); },
    // A3 — بدون تصنيف محلي: الشريحة تصل جاهزة من الخادم على كل عميل.
    classify: (c) => c.segment,
  },
];

export function getMethodology(id) {
  return METHODOLOGIES.find((m) => m.id === id) || METHODOLOGIES[0];
}

export function buildSegments(_methodologyId, _allCustomers) {
  return _buildDisplaySegments();
}
